import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { X, Plus, Trash2, ChevronDown, ChevronUp, Upload } from 'lucide-react';
import { ModuleForm } from './ModuleForm';
import { useDialogA11y } from '../ui/useDialogA11y';

interface CourseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  courseId?: string;
}

interface Module {
  id?: string;
  name: string;
  description: string;
  objective: string;
  start_date: string;
  end_date: string;
  zoom_link: string;
  order_index: number;
}

export function CourseModal({ isOpen, onClose, onSuccess, courseId }: CourseModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    docente_id: '',
    secondary_docente_id: '',
    status: 'active',
    thumbnail_url: '',
    evaluation_1_date: '',
    evaluation_2_date: '',
    evaluation_3_date: '',
    final_evaluation_date: '',
  });
  const [modules, setModules] = useState<Module[]>([]);
  const [docentes, setDocentes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModules, setShowModules] = useState(false);
  const [expandedModules, setExpandedModules] = useState<number[]>([]);
  const [uploading, setUploading] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string>('');
  const [deletedModuleIds, setDeletedModuleIds] = useState<string[]>([]);
  const [duplicateWarning, setDuplicateWarning] = useState('');
  const [forceSave, setForceSave] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const duplicateDialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(isOpen, onClose, dialogRef);
  useDialogA11y(showDuplicateModal, () => setShowDuplicateModal(false), duplicateDialogRef);

  useEffect(() => {
    if (isOpen) {
      loadDocentes();
      if (courseId) {
        loadCourse();
      } else {
        resetForm();
      }
    }
  }, [courseId, isOpen]);

  const loadDocentes = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .eq('role', 'docente')
        .eq('active', true);

      if (error) throw error;
      setDocentes(data || []);
    } catch (error) {
      console.error('Error loading docentes:', error);
    }
  };

  const loadCourse = async () => {
    try {
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('*')
        .eq('id', courseId)
        .single();

      if (courseError) throw courseError;

      if (courseData) {
        setFormData({
          name: courseData.name,
          description: courseData.description,
          start_date: courseData.start_date,
          end_date: courseData.end_date,
          docente_id: courseData.docente_id,
          secondary_docente_id: courseData.secondary_docente_id || '',
          status: courseData.status,
          thumbnail_url: courseData.thumbnail_url || '',
          evaluation_1_date: courseData.evaluation_1_date ? courseData.evaluation_1_date.split('T')[0] : '',
          evaluation_2_date: courseData.evaluation_2_date ? courseData.evaluation_2_date.split('T')[0] : '',
          evaluation_3_date: courseData.evaluation_3_date ? courseData.evaluation_3_date.split('T')[0] : '',
          final_evaluation_date: courseData.final_evaluation_date ? courseData.final_evaluation_date.split('T')[0] : '',
        });
        if (courseData.thumbnail_url) {
          setThumbnailPreview(courseData.thumbnail_url);
        }
      }

      const { data: modulesData, error: modulesError } = await supabase
        .from('modules')
        .select('*')
        .eq('course_id', courseId)
        .order('order_index');

      if (modulesError) throw modulesError;

      const formattedModules = (modulesData || []).map(m => ({
        ...m,
        start_date: m.start_date || '',
        end_date: m.end_date || '',
      }));

      setModules(formattedModules);
    } catch (error) {
      console.error('Error loading course:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      docente_id: '',
      secondary_docente_id: '',
      status: 'active',
      thumbnail_url: '',
      evaluation_1_date: '',
      evaluation_2_date: '',
      evaluation_3_date: '',
      final_evaluation_date: '',
    });
    setModules([]);
    setError('');
    setShowModules(false);
    setThumbnailFile(null);
    setThumbnailPreview('');
    setDeletedModuleIds([]);
    setDuplicateWarning('');
    setForceSave(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('La imagen no puede superar los 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Solo se permiten archivos de imagen');
        return;
      }

      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const uploadThumbnail = async (): Promise<string | null> => {
    if (!thumbnailFile) return null;

    setUploading(true);
    try {
      const fileExt = thumbnailFile.name.split('.').pop();
      const fileName = `course-${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('thumbnails')
        .upload(filePath, thumbnailFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('thumbnails')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const addModule = () => {
    const newModule: Module = {
      name: '',
      description: '',
      objective: '',
      start_date: '',
      end_date: '',
      zoom_link: '',
      order_index: modules.length + 1,
    };
    const newModules = [...modules, newModule];
    setModules(newModules);
    setExpandedModules([...expandedModules, newModules.length - 1]);
  };

  const toggleModule = (index: number) => {
    if (expandedModules.includes(index)) {
      setExpandedModules(expandedModules.filter((i) => i !== index));
    } else {
      setExpandedModules([...expandedModules, index]);
    }
  };

  const updateModule = (index: number, field: keyof Module, value: string | number) => {
    const updated = [...modules];
    updated[index] = { ...updated[index], [field]: value };
    setModules(updated);

    if (field === 'name') {
      setTimeout(() => {
        const moduleNames = updated.map(m => m.name.trim()).filter(name => name !== '');
        const duplicateMap = new Map<string, number>();

        moduleNames.forEach(name => {
          const normalizedName = name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          duplicateMap.set(normalizedName, (duplicateMap.get(normalizedName) || 0) + 1);
        });

        const duplicates: string[] = [];
        const seenOriginal = new Set<string>();

        moduleNames.forEach(name => {
          const normalizedName = name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          if (duplicateMap.get(normalizedName)! > 1 && !seenOriginal.has(normalizedName)) {
            duplicates.push(name);
            seenOriginal.add(normalizedName);
          }
        });

        if (duplicates.length > 0) {
          setDuplicateWarning(`Módulos duplicados detectados: ${duplicates.join(', ')}`);
        } else {
          setDuplicateWarning('');
        }
      }, 300);
    }
  };

  const removeModule = (index: number) => {
    const moduleToRemove = modules[index];
    if (moduleToRemove.id) {
      setDeletedModuleIds([...deletedModuleIds, moduleToRemove.id]);
    }
    setModules(modules.filter((_, i) => i !== index));
    setDuplicateWarning('');
  };

  const checkDuplicateModules = () => {
    const moduleNames = modules.map(m => m.name.trim()).filter(name => name !== '');
    const duplicateMap = new Map<string, number>();

    moduleNames.forEach(name => {
      const normalizedName = name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      duplicateMap.set(normalizedName, (duplicateMap.get(normalizedName) || 0) + 1);
    });

    const duplicates: string[] = [];
    const seenOriginal = new Set<string>();

    moduleNames.forEach(name => {
      const normalizedName = name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (duplicateMap.get(normalizedName)! > 1 && !seenOriginal.has(normalizedName)) {
        duplicates.push(name);
        seenOriginal.add(normalizedName);
      }
    });

    if (duplicates.length > 0) {
      setDuplicateWarning(`Módulos duplicados detectados: ${duplicates.join(', ')}`);
      return false;
    }
    setDuplicateWarning('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const hasDuplicates = !checkDuplicateModules();
    if (hasDuplicates && !forceSave) {
      setLoading(false);
      setShowDuplicateModal(true);
      return;
    }

    try {
      if (deletedModuleIds.length > 0) {
        for (const moduleId of deletedModuleIds) {
          await supabase
            .from('calendar_events')
            .delete()
            .eq('module_id', moduleId);

          await supabase
            .from('modules')
            .delete()
            .eq('id', moduleId);
        }
        setDeletedModuleIds([]);
      }
      let thumbnailUrl = formData.thumbnail_url;

      if (thumbnailFile) {
        const uploadedUrl = await uploadThumbnail();
        if (uploadedUrl) {
          thumbnailUrl = uploadedUrl;
        }
      }

      const courseData: any = {
        name: formData.name,
        description: formData.description,
        start_date: formData.start_date,
        end_date: formData.end_date,
        docente_id: formData.docente_id,
        status: formData.status,
        thumbnail_url: thumbnailUrl,
      };

      if (formData.secondary_docente_id) {
        courseData.secondary_docente_id = formData.secondary_docente_id;
      }
      if (formData.evaluation_1_date) {
        courseData.evaluation_1_date = formData.evaluation_1_date;
      }
      if (formData.evaluation_2_date) {
        courseData.evaluation_2_date = formData.evaluation_2_date;
      }
      if (formData.evaluation_3_date) {
        courseData.evaluation_3_date = formData.evaluation_3_date;
      }
      if (formData.final_evaluation_date) {
        courseData.final_evaluation_date = formData.final_evaluation_date;
      }

      let savedCourseId = courseId;

      if (courseId) {
        const { error } = await supabase
          .from('courses')
          .update(courseData)
          .eq('id', courseId);

        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('courses')
          .insert([courseData])
          .select()
          .single();

        if (error) throw error;
        savedCourseId = data.id;
      }

      if (modules.length > 0 && savedCourseId) {
        for (const module of modules) {
          const moduleData: any = {
            name: module.name,
            description: module.description,
            objective: module.objective,
            zoom_link: module.zoom_link,
            order_index: module.order_index,
            course_id: savedCourseId,
            docente_id: formData.docente_id,
            status: 'active',
          };

          if (module.start_date) {
            moduleData.start_date = module.start_date;
          }
          if (module.end_date) {
            moduleData.end_date = module.end_date;
          }

          let savedModuleId = module.id;

          if (module.id) {
            const { error } = await supabase
              .from('modules')
              .update(moduleData)
              .eq('id', module.id);

            if (error) throw error;
          } else {
            const { data, error } = await supabase
              .from('modules')
              .insert([moduleData])
              .select()
              .single();

            if (error) throw error;
            savedModuleId = data.id;
          }

          if (savedModuleId && (module.start_date || module.end_date)) {
            const eventTitle = `${formData.name} - ${module.name}`;
            const eventData: any = {
              title: eventTitle,
              description: module.description || '',
              event_type: 'class',
              module_id: savedModuleId,
              course_id: savedCourseId,
              created_by: formData.docente_id,
            };

            if (module.start_date) eventData.start_time = module.start_date;
            if (module.end_date) eventData.end_time = module.end_date;

            if (module.start_date && module.end_date) {
              const { data: existingEvent } = await supabase
                .from('calendar_events')
                .select('id')
                .eq('module_id', savedModuleId)
                .maybeSingle();

              if (existingEvent) {
                await supabase
                  .from('calendar_events')
                  .update(eventData)
                  .eq('id', existingEvent.id);
              } else {
                await supabase
                  .from('calendar_events')
                  .insert([eventData]);
              }
            }
          }
        }
      }

      if (savedCourseId) {
        const evaluationDates = [
          { date: formData.evaluation_1_date, title: 'Evaluación 1', type: 'exam' as const },
          { date: formData.evaluation_2_date, title: 'Evaluación 2', type: 'exam' as const },
          { date: formData.evaluation_3_date, title: 'Evaluación 3', type: 'exam' as const },
          { date: formData.final_evaluation_date, title: 'Evaluación Final', type: 'exam' as const },
        ];

        for (const evaluation of evaluationDates) {
          if (evaluation.date) {
            const evalTitle = `${formData.name} - ${evaluation.title}`;
            const evalStartTime = `${evaluation.date}T09:00:00`;
            const evalEndTime = `${evaluation.date}T12:00:00`;

            const { data: existingEvalEvent } = await supabase
              .from('calendar_events')
              .select('id')
              .eq('course_id', savedCourseId)
              .eq('title', evalTitle)
              .maybeSingle();

            const evalEventData = {
              title: evalTitle,
              description: `${evaluation.title} del curso ${formData.name}`,
              event_type: evaluation.type,
              start_time: evalStartTime,
              end_time: evalEndTime,
              course_id: savedCourseId,
              created_by: formData.docente_id,
            };

            if (existingEvalEvent) {
              await supabase
                .from('calendar_events')
                .update(evalEventData)
                .eq('id', existingEvalEvent.id);
            } else {
              await supabase
                .from('calendar_events')
                .insert([evalEventData]);
            }
          }
        }

        const evaluationTitles = evaluationDates
          .filter(e => e.date)
          .map(e => `${formData.name} - ${e.title}`);

        if (evaluationTitles.length > 0) {
          const { data: allEvalEvents } = await supabase
            .from('calendar_events')
            .select('id, title')
            .eq('course_id', savedCourseId)
            .eq('event_type', 'exam');

          if (allEvalEvents) {
            const eventsToDelete = allEvalEvents.filter(
              event => !evaluationTitles.includes(event.title)
            );

            for (const event of eventsToDelete) {
              await supabase
                .from('calendar_events')
                .delete()
                .eq('id', event.id);
            }
          }
        }
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Error saving course:', error);
      setError(error.message || 'Error al guardar el curso');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-course"
        tabIndex={-1}
        className="glass-surface rounded-xl max-w-4xl w-full border border-white/[0.06] max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06] glass-surface flex-shrink-0">
          <h2 id="modal-title-course" className="text-xl font-bold text-white">
            {courseId ? 'Editar Curso' : 'Nuevo Curso'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/[0.04] rounded-lg transition-all"
          >
            <X className="w-5 h-5 text-muted" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500 rounded-lg text-red-500 text-sm">
              {error}
            </div>
          )}


          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white/70 mb-2">
                Nombre del Curso *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-white/70 mb-2">
                Descripción *
              </label>
              <textarea
                required
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Imagen del Curso (opcional)
              </label>

              <div className="space-y-3">
                {(thumbnailPreview || formData.thumbnail_url) && (
                  <div className="relative w-full h-48 glass-deep rounded-lg overflow-hidden border border-white/[0.08]">
                    <img
                      src={thumbnailPreview || formData.thumbnail_url}
                      alt="Preview"
                      className="w-full h-full object-contain"
                    />
                    {thumbnailPreview && (
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-1 bg-cubus-lime text-cubus-bg rounded text-xs font-medium">
                          Nueva imagen
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <label className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-center gap-2 px-4 py-2 glass-deep hover:bg-cubus-lime/10 border border-white/[0.08] hover:border-cubus-lime/40 rounded-lg text-white/70 hover:text-cubus-lime transition-all">
                      <Upload className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {thumbnailFile ? thumbnailFile.name : 'Subir Imagen'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>

                  {(thumbnailPreview || thumbnailFile) && (
                    <button
                      type="button"
                      onClick={() => {
                        setThumbnailFile(null);
                        setThumbnailPreview('');
                        setFormData({ ...formData, thumbnail_url: '' });
                      }}
                      className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-all text-sm"
                    >
                      Quitar
                    </button>
                  )}
                </div>

                <div className="text-xs text-subtle">
                  <p>• Tamaño máximo: 5MB</p>
                  <p>• Formatos: JPG, PNG, WebP, GIF</p>
                  <p>• La imagen se ajustará automáticamente en la tarjeta</p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Fecha de Inicio *
              </label>
              <input
                type="date"
                required
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Fecha de Fin *
              </label>
              <input
                type="date"
                required
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Docente Principal *
              </label>
              <select
                required
                value={formData.docente_id}
                onChange={(e) => setFormData({ ...formData, docente_id: e.target.value })}
                className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
              >
                <option value="">Seleccionar docente principal</option>
                {docentes.map((docente) => (
                  <option key={docente.id} value={docente.id}>
                    {docente.full_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Docente Secundario (Opcional)
              </label>
              <select
                value={formData.secondary_docente_id}
                onChange={(e) => setFormData({ ...formData, secondary_docente_id: e.target.value })}
                className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
              >
                <option value="">Sin docente secundario</option>
                {docentes
                  .filter((docente) => docente.id !== formData.docente_id)
                  .map((docente) => (
                    <option key={docente.id} value={docente.id}>
                      {docente.full_name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Estado
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
              >
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
                <option value="completed">Completado</option>
                <option value="draft">Borrador</option>
              </select>
            </div>
          </div>

          <div className="border-t border-white/[0.08] pt-6">
            <h3 className="text-lg font-bold text-white mb-4">Fechas de Evaluaciones</h3>
            <p className="text-sm text-muted mb-4">
              Establece las fechas de entrega de las evaluaciones del curso. Estas fechas se sincronizarán automáticamente con el calendario.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Fecha Evaluación 1
                </label>
                <input
                  type="date"
                  value={formData.evaluation_1_date}
                  onChange={(e) => setFormData({ ...formData, evaluation_1_date: e.target.value })}
                  className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Fecha Evaluación 2
                </label>
                <input
                  type="date"
                  value={formData.evaluation_2_date}
                  onChange={(e) => setFormData({ ...formData, evaluation_2_date: e.target.value })}
                  className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Fecha Evaluación 3
                </label>
                <input
                  type="date"
                  value={formData.evaluation_3_date}
                  onChange={(e) => setFormData({ ...formData, evaluation_3_date: e.target.value })}
                  className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Fecha Evaluación Final
                </label>
                <input
                  type="date"
                  value={formData.final_evaluation_date}
                  onChange={(e) => setFormData({ ...formData, final_evaluation_date: e.target.value })}
                  className="w-full px-4 py-2 glass-deep border border-white/[0.08] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cubus-lime"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-white/[0.08] pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Módulos del Curso</h3>
              <button
                type="button"
                onClick={() => setShowModules(!showModules)}
                className="text-cubus-lime hover:text-[#c9ed00] text-sm font-medium"
              >
                {showModules ? 'Ocultar' : 'Mostrar'} ({modules.length})
              </button>
            </div>

            {showModules && (
              <div className="space-y-3">
                {modules.map((module, index) => {
                  const isExpanded = expandedModules.includes(index);
                  return (
                    <div key={index} className="glass-deep rounded-lg border border-white/[0.08] overflow-hidden">
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/[0.06] transition-colors"
                        onClick={() => toggleModule(index)}
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-cubus-lime" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted" />
                          )}
                          <h4 className="font-medium text-white">
                            Módulo {index + 1}
                            {module.name && `: ${module.name}`}
                          </h4>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeModule(index);
                          }}
                          className="p-2 text-red-400 hover:bg-red-500/10 rounded transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="p-4 pt-0 border-t border-white/[0.08]">
                          <ModuleForm
                            module={module}
                            index={index}
                            onUpdate={updateModule}
                            onRemove={removeModule}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={addModule}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 glass-deep hover:bg-cubus-lime/10 border border-dashed border-white/[0.1] hover:border-cubus-lime/40 rounded-lg text-muted hover:text-cubus-lime transition-all"
                >
                  <Plus className="w-5 h-5" />
                  Agregar Módulo
                </button>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-white/[0.08]">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || uploading}
              className="flex-1 px-4 py-2 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg rounded-lg transition-all font-semibold disabled:opacity-50"
            >
              {uploading
                ? 'Subiendo imagen...'
                : loading
                ? 'Guardando...'
                : courseId
                ? 'Actualizar Curso'
                : 'Crear Curso'}
            </button>
          </div>
        </form>
        </div>
      </div>

      {showDuplicateModal && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) { setShowDuplicateModal(false); setForceSave(false); } }}
          role="presentation"
        >
          <div
            ref={duplicateDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title-course-duplicate"
            tabIndex={-1}
            className="glass-surface rounded-xl max-w-md w-full mx-4 border border-yellow-500 shadow-2xl"
          >
            <div className="p-6 border-b border-white/[0.08]">
              <h3 id="modal-title-course-duplicate" className="text-xl font-bold text-yellow-500">Advertencia: Módulos Duplicados</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-white/70">
                Se detectaron los siguientes módulos duplicados:
              </p>
              <div className="p-3 bg-yellow-500/10 border border-yellow-500 rounded-lg">
                <p className="text-yellow-500 font-medium">{duplicateWarning.replace('Módulos duplicados detectados: ', '')}</p>
              </div>
              <p className="text-sm text-muted">
                ¿Deseas continuar guardando el curso de todas formas?
              </p>
            </div>
            <div className="flex gap-3 p-6 border-t border-white/[0.08]">
              <button
                type="button"
                onClick={() => {
                  setShowDuplicateModal(false);
                  setForceSave(false);
                }}
                className="flex-1 px-4 py-2 glass-deep hover:bg-white/[0.06] text-white/70 rounded-lg transition-all"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  setForceSave(true);
                  setShowDuplicateModal(false);
                  setDuplicateWarning('');
                  const form = document.querySelector('form');
                  if (form) {
                    form.requestSubmit();
                  }
                }}
                className="flex-1 px-4 py-2 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg rounded-lg transition-all font-semibold"
              >
                Guardar de todas formas
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
